import { useCallback, useEffect, useState } from "react";
import { Badge, Button, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import {
    deleteOperatorCompanyKnowledgeService,
    getOperatorCompanyKnowledgeService,
    saveOperatorCompanyKnowledgeService,
    saveOperatorCompanySoniaService,
} from "../../../../Services/CRMAdminServices/CRMOperator";

/**
 * Sonia's knowledge base for one broking business.
 *
 * Sonia nurtures each business's own clients by SMS, so she has to answer as
 * that business - their offers, their process, their words. Fintrack writes and
 * keeps this current as a paid service (the Sonia Knowledge Base line under
 * Services and fees); the business reads it in their own app but does not edit
 * it, which is the thing they are paying for.
 *
 * The profile at the top applies to every message. The entries below are the
 * answers themselves.
 */

type Entry = {
    knowledgeId: number;
    category: string;
    title: string;
    content: string;
    sortOrder: number;
    isActive: boolean;
    updatedOn?: string | null;
    updatedBy?: string | null;
};

type Profile = {
    businessIntro?: string | null;
    tone?: string | null;
    officeHours?: string | null;
    servicesOffered?: string | null;
    doNotSay?: string | null;
    bookingInstructions?: string | null;
    escalationEmail?: string | null;
    updatedOn?: string | null;
    updatedBy?: string | null;
};

const EMPTY_ENTRY = { knowledgeId: 0, category: "General", title: "", content: "", sortOrder: 0, isActive: true };

// Starters, so a new business is not a blank page. Fintrack edits the answers;
// the questions are the ones brokers' clients actually ask.
const SUGGESTED = [
    { category: "About the business", title: "Who are you and what do you do?" },
    { category: "About the business", title: "Where are you based and who do you help?" },
    { category: "Fees", title: "What do you charge?" },
    { category: "Process", title: "What happens after I book a call?" },
    { category: "Process", title: "What do I need to have ready?" },
    { category: "Objections", title: "I am already with a broker" },
    { category: "Objections", title: "I am not ready yet" },
    { category: "Booking", title: "How do I book a time?" },
];

const SoniaKnowledge = ({ companyId }: { companyId: string | number }) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [managed, setManaged] = useState(false);
    const [profile, setProfile] = useState<Profile>({});
    const [entries, setEntries] = useState<Entry[]>([]);
    const [draft, setDraft] = useState<typeof EMPTY_ENTRY>({ ...EMPTY_ENTRY });
    const [editingId, setEditingId] = useState<number | null>(null);

    const apply = (data: any) => {
        setProfile(data?.profile ?? {});
        setEntries(data?.entries ?? []);
        setManaged(!!data?.managed);
    };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const response: any = await getOperatorCompanyKnowledgeService(companyId);
            apply(response?.data);
        } catch (error: any) {
            toast.error(error?.response?.data?.message || "Could not load the knowledge base.");
        } finally {
            setLoading(false);
        }
    }, [companyId]);

    useEffect(() => { load(); }, [load]);

    const saveProfileField = async (field: keyof Profile, value: string) => {
        const next = { ...profile, [field]: value || null };
        setProfile(next);
        try {
            const response: any = await saveOperatorCompanySoniaService(companyId, {
                businessIntro: next.businessIntro ?? null,
                tone: next.tone ?? null,
                officeHours: next.officeHours ?? null,
                servicesOffered: next.servicesOffered ?? null,
                doNotSay: next.doNotSay ?? null,
                bookingInstructions: next.bookingInstructions ?? null,
                escalationEmail: next.escalationEmail ?? null,
            });
            apply(response?.data);
        } catch (error: any) {
            toast.error(error?.response?.data?.message || "Could not save that.");
        }
    };

    const saveEntry = async (entry: typeof EMPTY_ENTRY) => {
        if (!entry.title.trim()) {
            toast.error("Type the question first.");
            return;
        }
        setSaving(true);
        try {
            const response: any = await saveOperatorCompanyKnowledgeService(companyId, {
                knowledgeId: entry.knowledgeId,
                category: entry.category || "General",
                title: entry.title.trim(),
                content: entry.content,
                sortOrder: entry.sortOrder,
                isActive: entry.isActive,
            });
            apply(response?.data);
            setDraft({ ...EMPTY_ENTRY });
            setEditingId(null);
        } catch (error: any) {
            toast.error(error?.response?.data?.message || "Could not save that entry.");
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (entry: Entry) => {
        await saveEntry({
            knowledgeId: entry.knowledgeId,
            category: entry.category,
            title: entry.title,
            content: entry.content,
            sortOrder: entry.sortOrder,
            isActive: !entry.isActive,
        });
    };

    const removeEntry = async (entry: Entry) => {
        try {
            const response: any = await deleteOperatorCompanyKnowledgeService(companyId, entry.knowledgeId);
            apply(response?.data);
        } catch (error: any) {
            toast.error(error?.response?.data?.message || "Could not remove that entry.");
        }
    };

    const startNew = (category?: string, title?: string) => {
        setEditingId(null);
        setDraft({ ...EMPTY_ENTRY, category: category ?? "General", title: title ?? "" });
    };

    const grouped = entries.reduce((acc: Record<string, Entry[]>, e) => {
        const key = e.category || "General";
        (acc[key] = acc[key] || []).push(e);
        return acc;
    }, {});

    const missing = SUGGESTED.filter(
        (s) => !entries.some((e) => e.title.trim().toLowerCase() === s.title.toLowerCase())
    );

    const field = (label: string, key: keyof Profile, rows = 2, placeholder = "") => (
        <div className="mb-2">
            <label className="form-label mb-1" style={{ fontSize: "13px", fontWeight: 600 }}>{label}</label>
            <textarea
                className="form-control form-control-sm"
                rows={rows}
                defaultValue={(profile[key] as string) ?? ""}
                placeholder={placeholder}
                onBlur={(e) => saveProfileField(key, e.target.value)}
            />
        </div>
    );

    return (
        <div
            className="p-3 mb-3"
            style={{ borderRadius: "8px", boxShadow: "0 4px 8px rgba(0,0,0,0.1)", backgroundColor: "#fff" }}
        >
            <div className="d-flex align-items-center justify-content-between mb-3">
                <h6 className="f-w-700 mb-0">
                    Sonia knowledge base{entries.length > 0 ? ` (${entries.length})` : ""}
                </h6>
                <Badge color={managed ? "success" : "secondary"}>
                    {managed ? "Managed by Fintrack" : "Not on the managed service"}
                </Badge>
            </div>

            {loading ? (
                <div className="text-center py-3"><Spinner size="sm" /></div>
            ) : (
                <>
                    {!managed && (
                        <div className="alert alert-warning py-2" style={{ fontSize: "13px" }}>
                            The Sonia Knowledge Base service is switched off for this business. Anything written here
                            is kept, but Sonia will not use it until the service is turned on under Services and fees.
                        </div>
                    )}

                    <div className="row">
                        <div className="col-md-6">
                            {field("How Sonia introduces the business", "businessIntro", 3,
                                "We are a mortgage broking business in ... helping ...")}
                            {field("What they offer", "servicesOffered", 3,
                                "Home loans, refinancing, first home buyers ...")}
                            {field("How Sonia should sound", "tone", 2,
                                "Warm, short sentences, no jargon, never pushy.")}
                        </div>
                        <div className="col-md-6">
                            {field("When someone can be contacted", "officeHours", 2, "Mon to Fri, 9am to 5.30pm AEST")}
                            {field("How Sonia books a time", "bookingInstructions", 2,
                                "Offer the broker's booking link, two time options, then confirm.")}
                            {field("What Sonia must never say", "doNotSay", 3,
                                "Never quote a rate as final, never promise approval, never give credit advice.")}
                            <div className="mb-2">
                                <label className="form-label mb-1" style={{ fontSize: "13px", fontWeight: 600 }}>
                                    Where a conversation escalates to
                                </label>
                                <input
                                    type="email"
                                    className="form-control form-control-sm"
                                    defaultValue={profile.escalationEmail ?? ""}
                                    placeholder="owner@thebusiness.com.au"
                                    onBlur={(e) => saveProfileField("escalationEmail", e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    <small className="text-muted d-block mb-3">
                        Saves as soon as you leave a field.
                        {profile.updatedOn ? ` Last updated by ${profile.updatedBy || "Fintrack"}.` : ""}
                    </small>

                    <hr />

                    {/* The answers themselves */}
                    {Object.keys(grouped).sort().map((category) => (
                        <div key={category} className="mb-3">
                            <div className="f-w-600 mb-2" style={{ fontSize: "13px", color: "#444" }}>{category}</div>
                            {grouped[category].map((entry) => (
                                <div key={entry.knowledgeId} className="border rounded p-2 mb-2">
                                    {editingId === entry.knowledgeId ? (
                                        <EntryForm
                                            value={{
                                                knowledgeId: entry.knowledgeId,
                                                category: entry.category,
                                                title: entry.title,
                                                content: entry.content,
                                                sortOrder: entry.sortOrder,
                                                isActive: entry.isActive,
                                            }}
                                            saving={saving}
                                            onCancel={() => setEditingId(null)}
                                            onSave={saveEntry}
                                        />
                                    ) : (
                                        <>
                                            <div className="d-flex align-items-start justify-content-between">
                                                <div style={{ flex: 1 }}>
                                                    <div className="f-w-600" style={{ fontSize: "13.5px" }}>
                                                        {entry.title}
                                                        {!entry.isActive && (
                                                            <span className="text-muted f-w-400"> (off)</span>
                                                        )}
                                                    </div>
                                                    <div style={{ fontSize: "13px", color: "#555", whiteSpace: "pre-wrap" }}>
                                                        {entry.content}
                                                    </div>
                                                </div>
                                                <div className="d-flex gap-1">
                                                    <Button color="light" size="sm" onClick={() => setEditingId(entry.knowledgeId)}>Edit</Button>
                                                    <Button color="light" size="sm" onClick={() => toggleActive(entry)}>
                                                        {entry.isActive ? "Turn off" : "Turn on"}
                                                    </Button>
                                                    <Button color="light" size="sm" onClick={() => removeEntry(entry)}>Remove</Button>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    ))}

                    {/* Add one */}
                    <div className="border rounded p-2 mb-2" style={{ backgroundColor: "#fafafa" }}>
                        <EntryForm value={draft} saving={saving} onSave={saveEntry} onChange={setDraft} />
                    </div>

                    {missing.length > 0 && (
                        <div style={{ fontSize: "12.5px" }}>
                            <span className="text-muted">Still to answer: </span>
                            {missing.map((s) => (
                                <Button
                                    key={s.title}
                                    color="light"
                                    size="sm"
                                    className="me-1 mb-1"
                                    style={{ fontSize: "12px", padding: "2px 8px" }}
                                    onClick={() => startNew(s.category, s.title)}
                                >
                                    {s.title}
                                </Button>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

const EntryForm = ({
    value, saving, onSave, onCancel, onChange,
}: {
    value: { knowledgeId: number; category: string; title: string; content: string; sortOrder: number; isActive: boolean };
    saving: boolean;
    onSave: (entry: any) => void;
    onCancel?: () => void;
    onChange?: (entry: any) => void;
}) => {
    const [local, setLocal] = useState(value);

    useEffect(() => { setLocal(value); }, [value]);

    const set = (patch: any) => {
        const next = { ...local, ...patch };
        setLocal(next);
        onChange?.(next);
    };

    return (
        <>
            <div className="d-flex gap-2 mb-2">
                <input
                    className="form-control form-control-sm"
                    style={{ maxWidth: "200px" }}
                    placeholder="Category"
                    value={local.category}
                    onChange={(e) => set({ category: e.target.value })}
                />
                <input
                    className="form-control form-control-sm"
                    placeholder="The question, as a client would ask it"
                    value={local.title}
                    onChange={(e) => set({ title: e.target.value })}
                />
            </div>
            <textarea
                className="form-control form-control-sm mb-2"
                rows={3}
                placeholder="What Sonia should say back, in the business's own words."
                value={local.content}
                onChange={(e) => set({ content: e.target.value })}
            />
            <div className="d-flex gap-2">
                <Button color="primary" size="sm" disabled={saving} onClick={() => onSave(local)}>
                    {saving ? "Saving . . ." : local.knowledgeId ? "Save" : "Add"}
                </Button>
                {onCancel && <Button color="light" size="sm" onClick={onCancel}>Cancel</Button>}
            </div>
        </>
    );
};

export default SoniaKnowledge;
